/**
 * DomainFilter — hierarchické filtrování podle domén.
 *
 * Zobrazuje domény rozepsané po úrovních (2., 3., 4. řád…).
 * Kliknutím na skupinu se filtr zúží. Breadcrumb umožňuje navigaci zpět.
 *
 * Použití:
 *   const [domainFilter, setDomainFilter] = useState<string | null>(null);
 *
 *   // Filtr na hosty:
 *   const filtered = hosts.filter(h =>
 *     matchesDomainFilter(h.domain_names, domainFilter)
 *   );
 *
 *   <DomainFilter
 *     domains={hosts.flatMap(h => h.domain_names)}
 *     selected={domainFilter}
 *     onChange={setDomainFilter}
 *   />
 *
 * matchesDomainFilter je exportován pro použití ve filtrovací logice.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, X } from 'lucide-react';
import { cn } from '../../lib/utils';

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Vrátí true pokud alespoň jedna z domén patří pod daný suffix.
 * Např. "api.services.maxcloud.cz" patří pod "services.maxcloud.cz" i "maxcloud.cz".
 */
export function matchesDomainFilter(domainNames: string[], filter: string | null): boolean {
  if (!filter) return true;
  return domainNames.some((d) => d === filter || d.endsWith('.' + filter));
}

/**
 * Vrátí řetěz suffixů od nejkratšího (2. řád) po celou doménu.
 * "api.services.maxcloud.cz" → ["maxcloud.cz", "services.maxcloud.cz", "api.services.maxcloud.cz"]
 */
function getBreadcrumb(domain: string): string[] {
  const parts = domain.split('.');
  const result: string[] = [];
  for (let depth = 2; depth <= parts.length; depth++) {
    result.push(parts.slice(parts.length - depth).join('.'));
  }
  return result;
}

interface Group {
  suffix: string;
  count: number;
}

/**
 * Vrátí skupiny pro další úroveň pod `selected` (nebo 2. řád pokud selected = null).
 * count = počet domén z `all` které patří do skupiny.
 */
function computeGroups(all: string[], selected: string | null): Group[] {
  const targetDepth = selected ? selected.split('.').length + 1 : 2;
  const map = new Map<string, number>();

  for (const domain of all) {
    const parts = domain.split('.');
    if (parts.length < targetDepth) continue;

    const suffix = parts.slice(parts.length - targetDepth).join('.');

    if (selected && !suffix.endsWith('.' + selected)) continue;

    map.set(suffix, (map.get(suffix) ?? 0) + 1);
  }

  return Array.from(map.entries())
    .map(([suffix, count]) => ({ suffix, count }))
    .sort((a, b) => b.count - a.count || a.suffix.localeCompare(b.suffix));
}

// ── Component ──────────────────────────────────────────────────────────────────

interface DomainFilterProps {
  /** Plochý seznam všech domén (např. hosts.flatMap(h => h.domain_names)) */
  domains: string[];
  /** Aktuálně vybraný suffix nebo null */
  selected: string | null;
  /** Callback při výběru / zrušení */
  onChange: (suffix: string | null) => void;
  className?: string;
}

export function DomainFilter({ domains, selected, onChange, className }: DomainFilterProps) {
  const { t } = useTranslation();
  const { groups, breadcrumb } = useMemo(() => {
    const groups = computeGroups(domains, selected);
    const breadcrumb = selected ? getBreadcrumb(selected) : [];
    return { groups, breadcrumb };
  }, [domains, selected]);

  // Vůbec nezobrazujeme pokud nejsou aspoň 2 skupiny a nic není vybráno
  if (!selected && groups.length < 2) return null;

  const hasSubGroups = groups.length > 0;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Breadcrumb navigace */}
      {breadcrumb.length > 0 && (
        <div className='flex items-center gap-1 flex-wrap min-w-0'>
          <button
            onClick={() => onChange(null)}
            className='text-xs text-gray-500 hover:text-gray-300 transition-colors shrink-0'
          >
            {t('widgets.catAll', 'Vše')}
          </button>
          {breadcrumb.map((crumb) => (
            <span key={crumb} className='flex items-center gap-1 min-w-0'>
              <ChevronRight size={10} className='text-gray-700 shrink-0' />
              <button
                onClick={() =>
                  onChange(crumb)}
                className={cn(
                  'text-xs transition-colors truncate max-w-[180px]',
                  crumb === selected
                    ? 'text-plex font-medium cursor-default'
                    : 'text-gray-400 hover:text-gray-200',
                )}
                title={crumb}
              >
                {crumb}
              </button>
            </span>
          ))}
          {/* Reset */}
          <button
            onClick={() => onChange(null)}
            className='ml-0.5 p-0.5 rounded text-gray-600 hover:text-gray-300 hover:bg-gray-700 transition-colors shrink-0'
            title={t('ui.clearFilter', 'Zrušit filtr')}
          >
            <X size={10} />
          </button>
        </div>
      )}

      {/* Skupiny — klikatelné badge */}
      {hasSubGroups && (
        <div className='flex flex-wrap gap-1.5'>
          {groups.map(({ suffix, count }) => {
            const isActive = suffix === selected;
            // Zkrácení: zobraz jen část navíc oproti selected
            const label = selected
              ? suffix.slice(0, suffix.length - selected.length - 1) // "api" z "api.services.maxcloud.cz"
              : suffix;

            return (
              <button
                key={suffix}
                onClick={() => onChange(isActive ? null : suffix)}
                title={suffix}
                className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border',
                  'transition-all duration-100',
                  isActive
                    ? 'bg-plex/20 border-plex/40 text-plex'
                    : 'bg-gray-800/60 border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-200 hover:bg-gray-800',
                )}
              >
                <span className='font-mono'>{label}</span>
                <span
                  className={cn(
                    'px-1.5 py-0.5 rounded-md text-[10px] font-medium leading-none',
                    isActive ? 'bg-plex/20 text-plex' : 'bg-gray-700/80 text-gray-500',
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Leaf level — není co rozbalit */}
      {!hasSubGroups && selected && (
        <p className='text-[11px] text-gray-600 italic'>{t('ui.deepestLevel', 'Nejhlubší úroveň')}</p>
      )}
    </div>
  );
}
